import React from 'react'
import copy from './copy'
import { useHandleCopyValue } from '@sketch/modules-common'
import {
  AttributeList,
  FullCopyAttribute,
  Blend,
  CollapsibleInspectorSection,
} from '../../components'
import { ReactComponent as RectanglesIcon } from '@sketch/icons/rectangles-2-17'
import { Appearance as LayerAppearance } from '../../../../../../inspector'
import { ProgressiveAlpha } from '../../components/ProgressiveAlpha'

type AppearanceProps = {
  appearance: LayerAppearance
}

export const Appearance: React.FC<AppearanceProps> = ({ appearance }) => {
  const { blendMode, opacity } = appearance
  const opacityPercentage = opacity * 100
  const opacityPercentageString = `${opacityPercentage.toFixed().toLocaleString()}%`

  // Calculate progressive alpha values if applicable
  const isProgressive = appearance.isProgressive
  const progressiveFrom =
    (appearance.gradient?.stops[0].color.alpha ?? 1) * opacityPercentage
  const progressiveTo =
    (appearance.gradient?.stops.slice(-1)[0]?.color.alpha ?? 1) *
    opacityPercentage

  const copyValue = isProgressive
    ? `${progressiveFrom} -> ${progressiveTo}`
    : opacityPercentageString

  const sectionCopyValue = copy({
    opacity: copyValue,
    blendMode,
  })

  const { handleCopyValue, copyTooltipText } =
    useHandleCopyValue(sectionCopyValue)

  return (
    <CollapsibleInspectorSection
      title="Appearance"
      data-testid="inspector-sidebar-appearance"
      actions={[
        {
          icon: <RectanglesIcon width={17} height={17} />,
          tooltipText: copyTooltipText,
          onClick: handleCopyValue,
        },
      ]}
    >
      <AttributeList>
        <FullCopyAttribute
          label="Opacity"
          value={
            <>
              {isProgressive ? (
                <ProgressiveAlpha from={progressiveFrom} to={progressiveTo} />
              ) : (
                opacityPercentageString
              )}

              {blendMode.toLowerCase() !== 'normal' && (
                <Blend blendMode={blendMode} />
              )}
            </>
          }
          copyValue={copyValue}
        />
      </AttributeList>
    </CollapsibleInspectorSection>
  )
}
